import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { useActionHook, useModals } from "hooks";
import { filter, some, uniqBy } from "lodash";
import { useAllFilesSelect, useDocumentsActions, useFoldersSelect } from "store";
import { DealBase, IAiFile, IFile, IFileUploadType, IFolder } from "store/types";

import { IFilesPickerContext, ISelectedItem, ISelectState, SelectItemTypes } from "./files-picker-context";

interface UseFilesPickerProps {
  currentFiles: IAiFile[];
  isModalOpen: boolean;
  singleFileSelection?: boolean;
  dealId: number | null;
  getFilesOutsideDeal?: boolean;
  fileUploadType?: IFileUploadType[];
  attachedFiles?: File[];
  selectWithoutFiles?: boolean;
  acceptedFilesExtensions?: string[];
}

const DEFAULT_STATE: ISelectState = { folders: [], files: [] };

const useFilesAndSelection = (
  resultFiles: IFile[],
  resultFolders: IFolder[],
  currentFiles: IAiFile[],
  singleFileSelection?: boolean,
  getFilesOutsideDeal?: boolean,
  acceptedFilesExtensions?: string[]
) => {
  const [state, setState] = useState<ISelectState>(DEFAULT_STATE);

  const [attachments, setAttachments] = useState<File[]>([]);
  const [oldFiles, setOldFiles] = useState<IAiFile[]>([]);
  const [deletedFilesIds, setDeletedFilesIds] = useState<number[]>([]);

  const isItemSelected = useCallback(
    (id: number, kind: keyof ISelectState) => {
      return state[kind].some((el) => el.id === id);
    },
    [state]
  );

  const selectItem = useCallback(
    (item: ISelectedItem) => {
      if (singleFileSelection) {
        if (attachments.length > 0) return;

        if (item.id && !isItemSelected(item.id, item.kind)) {
          setState((prev) => ({
            ...prev,
            [item.kind]: [item]
          }));
          setDeletedFilesIds((prev) => prev.filter((el) => el !== item.id));
          setOldFiles([]);
        } else {
          setState((prev) => ({
            ...prev,
            [item.kind]: []
          }));
          setOldFiles([]);
        }
      } else {
        if (item.id && !isItemSelected(item.id, item.kind)) {
          setState((prev) => ({
            ...prev,
            [item.kind]: [...prev[item.kind], item]
          }));
          setDeletedFilesIds((prev) => prev.filter((el) => el !== item.id));
        } else {
          setState((prev) => ({
            ...prev,
            [item.kind]: prev[item.kind].filter((el) => el.id !== item.id)
          }));
          setOldFiles((prevState) => prevState.filter((el) => el.id !== item.id));
        }
      }
    },
    [resultFolders, isItemSelected, singleFileSelection, attachments.length]
  );

  const setCurrentFilesToSelected = useCallback(() => {
    if (!currentFiles) return;
    const files = filter(resultFiles, (item) => some(currentFiles, { id: item.id }));

    const availableFiles = getFilesOutsideDeal
      ? files
      : files.filter((item) => resultFolders.map((folder) => folder.id).includes(item.section_id));

    setState((prevState) => ({
      ...prevState,
      files: [
        ...prevState.files,
        ...(availableFiles || []).map((el) => ({
          id: el.id,
          kind: "files" as SelectItemTypes,
          parentId: el.section_id
        }))
      ]
    }));
  }, [currentFiles, resultFiles, resultFolders, getFilesOutsideDeal]);

  const addFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target?.files;
      if (files) {
        // Validate file types based on acceptFilesExtensions
        const validFiles = Array.from(files).filter((file) => {
          // If acceptFilesExtensions is provided, use it for validation
          if (acceptedFilesExtensions && acceptedFilesExtensions.length) {
            return acceptedFilesExtensions.some((extension) =>
              file.name.toLowerCase().endsWith(extension.toLowerCase())
            );
          }

          // Default behavior: allow images and PDFs
          const isValidMimeType = file.type.startsWith("image/") || file.type === "application/pdf";
          const isValidExtension =
            file.name.toLowerCase().endsWith(".pdf") || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(file.name);

          return isValidMimeType || isValidExtension;
        });

        if (validFiles.length === 0) {
          return;
        }

        if (singleFileSelection) {
          setAttachments([validFiles[0]]);
          setState(DEFAULT_STATE);
          setOldFiles([]);
        } else {
          setAttachments([...attachments, ...validFiles]);
        }

        // Clear the input value to allow the same file to be selected again
        e.target.value = "";
      }
    },
    [attachments, singleFileSelection, acceptedFilesExtensions]
  );

  //we dont have id for local files, so we use lastModified+name+size to identify the file
  const removeFile = useCallback(
    (file: File) => {
      const getFileMetadata = (file: File) => `${file.lastModified}-${file.name}-${file.size}`;
      setAttachments(attachments.filter((item) => getFileMetadata(item) !== getFileMetadata(file)));
    },
    [attachments]
  );

  const removeOldFile = useCallback(
    (file: IAiFile) => {
      setOldFiles(oldFiles.filter((item) => item.id !== file.id));
      setDeletedFilesIds((prev) => [...prev, file.id]);
      const fileFromDeal = resultFiles.find((item) => item.id === file.id);
      fileFromDeal && selectItem({ id: file.id, kind: "files", parentId: fileFromDeal?.section_id });
    },
    [oldFiles, resultFiles, selectItem]
  );

  const invalidateData = () => {
    setAttachments([]);
    setOldFiles([]);
    setDeletedFilesIds([]);
  };

  const selectAllInFolder = useCallback(
    (files: IFile[]) => {
      const areAllSelected = files.every((file) => isItemSelected(file.id, "files"));

      if (areAllSelected) {
        setState((prev) => ({
          ...prev,
          files: prev.files.filter((item) => !files.some((file) => file.id === item.id))
        }));
        setOldFiles((prev) => prev.filter((item) => !files.some((file) => file.id === item.id)));
      } else {
        const newFiles = files
          .filter((file) => !isItemSelected(file.id, "files"))
          .map((file) => ({
            id: file.id,
            kind: "files" as const,
            parentId: file.section_id
          }));

        setState((prev) => ({
          ...prev,
          files: [...prev.files, ...newFiles]
        }));
      }
    },
    [isItemSelected]
  );

  const isSelectionDisabled = useCallback(
    (id?: number) => {
      if (!singleFileSelection) return false;
      if (attachments.length > 0) return true;

      if (id !== undefined) {
        const isCurrentlySelected = state.files.some((file) => file.id === id);
        if (isCurrentlySelected) return false;
      }

      return state.files.length > 0;
    },
    [singleFileSelection, attachments.length, state.files]
  );

  useEffect(() => {
    const findAllItemParents = (item: ISelectedItem): ISelectedItem[] => {
      const parentFolder = resultFolders.find((el) => el.id === item.parentId);
      const parentFolders: ISelectedItem[] = [];

      const selectFolders = (folder: IFolder) => {
        parentFolders.push({ id: folder.id, kind: "folders", parentId: folder.parent_id });
        if (folder.parent_id) {
          const parentFolder = resultFolders.find((el) => el.id === folder.parent_id);
          parentFolder && selectFolders(parentFolder);
        }
      };

      parentFolder && selectFolders(parentFolder);
      return parentFolders;
    };

    const allParents = uniqBy(state.files.map((item) => findAllItemParents(item)).flat(), "id");

    const currentFolderIds = new Set(state.folders.map((f) => f.id));
    const newFolderIds = new Set(allParents.map((f) => f.id));

    if (
      allParents.length !== state.folders.length ||
      !Array.from(currentFolderIds).every((id) => newFolderIds.has(id))
    ) {
      setState((prevState) => ({
        ...prevState,
        folders: allParents
      }));
    }
  }, [state.files, resultFolders]);

  const selectedItemsCounter = useMemo(() => state.files.length + attachments.length, [state.files, attachments]);

  return {
    state,
    setState,
    isItemSelected,
    selectItem,
    setCurrentFilesToSelected,
    selectedItemsCounter,

    attachments,
    setAttachments,
    oldFiles,
    setOldFiles,
    deletedFilesIds,
    setDeletedFilesIds,
    addFile,
    removeFile,
    removeOldFile,
    invalidateData,
    selectAllInFolder,
    isSelectionDisabled,
    singleFileSelection
  };
};

export const useFilesPickerContextHook = ({
  currentFiles,
  isModalOpen,
  singleFileSelection,
  dealId,
  getFilesOutsideDeal = false,
  fileUploadType = [],
  attachedFiles,
  acceptedFilesExtensions
}: UseFilesPickerProps): IFilesPickerContext => {
  const [step, setStep] = useState<"select-deal" | "select-files">(dealId ? "select-files" : "select-deal");
  const [deal, setDeal] = useState<DealBase | null>(null);
  const [loading, setLoading] = useState(false);

  // const dealIdFromStore = useDealIdSelector(); // TODO: need only to check if we stay outside of deal in case to get deal files outside

  const { folders, parentFolders } = useFoldersSelect({ kind: "folder" }).reduce(
    (acc, item) => {
      if (item.parent_id) {
        acc.folders.push(item);
      } else {
        acc.parentFolders.push(item);
      }
      return acc;
    },
    { folders: [] as IFolder[], parentFolders: [] as IFolder[] }
  );

  const { folders: dataroomChildFolders, parentFolders: dataroomParentFolders } = useFoldersSelect({
    kind: "dataroom"
  }).reduce(
    (acc, item) => {
      if (item.parent_id) {
        acc.folders.push(item);
      } else {
        acc.parentFolders.push(item);
      }
      return acc;
    },
    { folders: [] as IFolder[], parentFolders: [] as IFolder[] }
  );

  const aiFolders = useFoldersSelect({ kind: "ai-files" });
  const allFiles = useAllFilesSelect();

  const allFolders = useMemo(
    () => [...folders, ...parentFolders, ...aiFolders, ...dataroomChildFolders],
    [folders, parentFolders, aiFolders, dataroomChildFolders]
  );

  const { errorModal } = useModals();
  const { getDealFoldersAction, getDealFilesAction } = useDocumentsActions();

  const {
    action: getDealFiles,
    loading: filesLoading,
    data: filesFromAPI
  } = useActionHook(getDealFilesAction, {
    onReject: () => errorModal({ msg: "Something went wrong" })
  });

  const {
    action: getDealFolders,
    loading: foldersLoading,
    data: foldersFromAPI
  } = useActionHook(getDealFoldersAction, {
    onSuccess: () => {
      deal?.id && getDealFiles(deal.id);
    },
    onReject: () => errorModal({ msg: "Something went wrong" })
  });

  useEffect(() => {
    if (getFilesOutsideDeal && isModalOpen && dealId) {
      getDealFolders({ dealId: dealId });
      getDealFiles(dealId);
    }
  }, [getFilesOutsideDeal, isModalOpen, dealId]);

  const resultFiles = useMemo(() => {
    const isOutsideDealContext = (getFilesOutsideDeal && dealId) || deal?.id;
    const files = isOutsideDealContext ? Object.values(filesFromAPI || {}) : allFiles;

    return files.filter((file) => {
      const hasFileUrl = file.file_url;
      const hasSpecificTypes = fileUploadType.length ? fileUploadType.includes(file.upload_type) : true;

      return hasFileUrl && hasSpecificTypes;
    });
  }, [filesFromAPI, allFiles, deal?.id, dealId, getFilesOutsideDeal, fileUploadType]);

  const resultFolders = useMemo(() => {
    const folders = (getFilesOutsideDeal && dealId) || deal?.id ? Object.values(foldersFromAPI || {}) : allFolders;

    return folders.filter((folder) => folder.kind === "folder" || folder.kind === "dataroom");
  }, [foldersFromAPI, allFolders, deal?.id, dealId, getFilesOutsideDeal]);

  const parentFoldersToRender = useMemo(() => {
    const isValidFolderKind = (folder: IFolder) => folder.kind === "folder" || folder.kind === "dataroom";

    const isRootFolder = (folder: IFolder) => folder.parent_id === null && isValidFolderKind(folder);

    if (getFilesOutsideDeal && dealId) {
      return Object.values(foldersFromAPI || {}).filter(isRootFolder);
    }

    if (dealId) {
      return [...parentFolders, ...dataroomParentFolders];
    }

    return Object.values(foldersFromAPI || {}).filter(isRootFolder);
  }, [dealId, parentFolders, dataroomParentFolders, foldersFromAPI, getFilesOutsideDeal]);

  const filesAndSelection = useFilesAndSelection(
    resultFiles,
    resultFolders,
    currentFiles,
    singleFileSelection,
    getFilesOutsideDeal,
    acceptedFilesExtensions
  );

  const resetState = useCallback(() => {
    filesAndSelection.setState(DEFAULT_STATE);
    filesAndSelection.setAttachments([]);
    filesAndSelection.setDeletedFilesIds([]);
    setStep(dealId ? "select-files" : "select-deal");
    setDeal(null);
  }, [dealId, filesAndSelection, setDeal]);

  const preselectFiles = useCallback(() => {
    filesAndSelection.setCurrentFilesToSelected();
    filesAndSelection.setOldFiles(currentFiles || []);
    filesAndSelection.setAttachments(attachedFiles || []);
  }, [currentFiles, filesAndSelection, attachedFiles]);

  useEffect(() => {
    if (isModalOpen && step === "select-files" && resultFiles.length) {
      preselectFiles();
    }
  }, [isModalOpen, step, resultFiles.length]);

  useEffect(() => {
    if (currentFiles?.length) {
      filesAndSelection.setOldFiles(currentFiles);
    } else {
      filesAndSelection.setOldFiles([]);
    }
  }, [currentFiles]);

  return {
    ...filesAndSelection,
    step,
    setStep,
    deal,
    setDeal,
    loading,
    setLoading,
    foldersLoading,
    getDealFolders,
    filesLoading,
    files: resultFiles,
    parentFoldersToRender,
    resetState,
    preselectFiles,
    invalidateDataStates: filesAndSelection.invalidateData,
    resultFolders,
    acceptedFilesExtensions
  };
};
